import React, { createContext, useContext, useState, useEffect, useRef, useMemo, ReactNode } from 'react';
import {
  SiteData,
  SitePage,
  PageSection,
  SectionType,
  IndustryType,
  SubscriptionPlanId,
  AutosaveStatus,
  SiteVersion,
  AISitePlan,
  SiteCommunication,
  CommunicationStatus,
  SectorQuotaInfo,
  SectorQuotas,
} from '../types';
import { realtimeDbService as dbService } from '../services/realtimeDbService';
import { communicationService } from '../services/communicationService';
import { PublishPipelineService } from '../services/publishService';
import { SUBSCRIPTION_PLANS } from '../config/constants';
import { QuotaMigrationService } from '../services/quotaMigrationService';
import { useAuth } from './AuthContext';
import confetti from 'canvas-confetti';

export type DeviceMode = 'desktop' | 'tablet' | 'mobile';

export interface ToastNotification {
  id: string;
  type: 'success' | 'info' | 'warning' | 'error';
  message: string;
}

interface SiteContextType {
  sites: SiteData[];
  activeSite: SiteData | null;
  activePageId: string;
  selectedSectionId: string | null;
  deviceMode: DeviceMode;
  testViewportWidth: number;
  isPreviewMode: boolean;
  isPublishing: boolean;
  hasUnsavedChanges: boolean;
  isLoadingSites: boolean;
  autosaveStatus: AutosaveStatus;
  siteVersions: SiteVersion[];
  
  // Undo / Redo & Toasts
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  toasts: ToastNotification[];
  showToast: (message: string, type?: 'success' | 'info' | 'warning' | 'error') => void;

  // Communications State & Actions
  communications: SiteCommunication[];
  unreadCommunicationsCount: number;
  communicationCount: number;
  loadCommunications: () => Promise<void>;
  createCommunication: (
    data: Omit<SiteCommunication, 'id' | 'createdAt' | 'status'> & { status?: CommunicationStatus }
  ) => Promise<SiteCommunication>;
  updateCommunicationStatus: (
    communicationId: string,
    status: CommunicationStatus,
    adminNotes?: string
  ) => Promise<void>;
  deleteCommunication: (communicationId: string) => Promise<void>;

  // Actions
  setActiveSite: (site: SiteData) => void;
  setActiveSiteById: (siteId: string) => Promise<void>;
  setActivePageId: (pageId: string) => void;
  setSelectedSectionId: (sectionId: string | null) => void;
  setDeviceMode: (mode: DeviceMode) => void;
  setTestViewportWidth: (width: number) => void;
  setIsPreviewMode: (preview: boolean) => void;
  
  // Site CRUD
  createNewSite: (
    name: string,
    subdomain: string,
    industry: IndustryType,
    templateId: string,
    planId: SubscriptionPlanId
  ) => Promise<SiteData>;
  createSiteWithAI: (
    sitePlan: AISitePlan,
    planId?: SubscriptionPlanId
  ) => Promise<SiteData>;
  updateSiteData: (updatedSite: Partial<SiteData>) => Promise<SiteData>;
  deleteCurrentSite: () => Promise<void>;
  publishCurrentSite: () => Promise<void>;
  unpublishCurrentSite: () => Promise<void>;
  rollbackToVersion: (versionId: string) => Promise<void>;
  loadSiteVersions: () => Promise<void>;
  
  // Section Operations
  addSectionToActivePage: (type: SectionType, targetIndex?: number) => void;
  addCustomSectionToActivePage: (section: PageSection, targetIndex?: number) => void;
  updateSectionInActivePage: (sectionId: string, contentUpdates?: any, styleUpdates?: any) => void;
  moveSectionInActivePage: (fromIndex: number, toIndex: number) => void;
  duplicateSectionInActivePage: (sectionId: string) => void;
  toggleSectionVisibility: (sectionId: string) => void;
  deleteSectionInActivePage: (sectionId: string) => void;

  // Page Operations
  addPageToSite: (title: string, slug: string, seoTitle?: string, seoDescription?: string) => void;
  updatePageInSite: (pageId: string, updates: Partial<SitePage>) => void;
  deletePageFromSite: (pageId: string) => void;
  
  // Save / Sync
  saveDraft: () => Promise<void>;
  refreshSites: () => Promise<void>;
  reloadSites: () => Promise<void>;
  setActiveSiteId: (siteId: string) => Promise<void>;
  deleteSite: (siteId: string) => Promise<void>;
  createSite: (
    name: string,
    subdomain: string,
    industry: IndustryType,
    templateId: string,
    planId: SubscriptionPlanId
  ) => Promise<SiteData>;
  yachtCount: number;
  reservationCount: number;
  blogCount: number;
  websiteLimit: number;
  remainingWebsiteQuota: number;
  canCreateWebsite: boolean;
  sectorQuotas: SectorQuotas;
  canCreateSectorWebsite: (sector: IndustryType) => boolean;
  totalRemainingQuota: number;
  refreshSectorQuotas: () => Promise<void>;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export const SiteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser, isSuperAdmin } = useAuth();
  const [sites, setSites] = useState<SiteData[]>([]);
  const [activeSite, setActiveSite] = useState<SiteData | null>(null);
  const [activePageId, setActivePageId] = useState<string>('');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [deviceMode, setDeviceModeState] = useState<DeviceMode>('desktop');
  const [testViewportWidth, setTestViewportWidthState] = useState<number>(1280);
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);

  const setDeviceMode = (mode: DeviceMode) => {
    setDeviceModeState(mode);
    if (mode === 'mobile') {
      setTestViewportWidthState(390);
    } else if (mode === 'tablet') {
      setTestViewportWidthState(768);
    } else {
      setTestViewportWidthState(1280);
    }
  };

  const setTestViewportWidth = (width: number) => {
    setTestViewportWidthState(width);
    if (width < 768) {
      setDeviceModeState('mobile');
    } else if (width < 1024) {
      setDeviceModeState('tablet');
    } else {
      setDeviceModeState('desktop');
    }
  };
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isLoadingSites, setIsLoadingSites] = useState<boolean>(true);
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('SAVED');
  const [siteVersions, setSiteVersions] = useState<SiteVersion[]>([]);
  const [communications, setCommunications] = useState<SiteCommunication[]>([]);
  const [yachtCount, setYachtCount] = useState<number>(0);
  const [reservationCount, setReservationCount] = useState<number>(0);
  const [blogCount, setBlogCount] = useState<number>(0);

  const unreadCommunicationsCount = communications.filter((c) => c.status === 'NEW' && !c.isDeleted).length;
  const communicationCount = communications.filter((c) => !c.isDeleted).length;

  // Sector-based Quotas & Entitlements State (Strict Source of Truth: Database Subscriptions)
  const [sectorQuotas, setSectorQuotas] = useState<SectorQuotas>({
    CORPORATE: {
      sector: 'CORPORATE',
      title: 'Kurumsal Web Sitesi',
      allowed: false,
      total: 0,
      used: 0,
      remaining: 0,
      status: 'NO_PLAN',
      planName: 'Paketsiz',
      planPriceTRY: 15000,
      planId: 'PLAN_CORP_ANNUAL',
      features: ['Website oluşturmak için bir paket satın alın.', 'AI Generator & Görsel Editör', 'Özel Domain & SSL'],
    },
    YACHT_RENTAL: {
      sector: 'YACHT_RENTAL',
      title: 'Lüks Yat Kiralama',
      allowed: false,
      total: 0,
      used: 0,
      remaining: 0,
      status: 'NO_PLAN',
      planName: 'Paketsiz',
      planPriceTRY: 36000,
      planId: 'PLAN_YACHT_ANNUAL',
      features: ['Website oluşturmak için bir paket satın alın.', 'Yat Filosu & Donanım', 'Müsaitlik & Rezervasyon'],
    },
  });

  const refreshSectorQuotas = async () => {
    if (!currentUser) return;
    try {
      const q = await dbService.getSectorQuotas(currentUser.uid, false);
      setSectorQuotas(q);
    } catch (e) {
      console.warn('Could not load sector quotas:', e);
    }
  };

  const canCreateSectorWebsite = (sector: IndustryType): boolean => {
    const q = sectorQuotas[sector];
    return Boolean(q && q.allowed && q.remaining > 0);
  };

  const activeSitesCount = useMemo(() => {
    return sites.filter((s) => !s.isDeleted).length;
  }, [sites]);

  const totalRemainingQuota = useMemo(() => {
    return (sectorQuotas.CORPORATE?.remaining || 0) + (sectorQuotas.YACHT_RENTAL?.remaining || 0);
  }, [sectorQuotas]);

  // Backward compatibility: Website Quota State strictly reflecting customer entitlement
  const websiteLimit = useMemo(() => {
    return (sectorQuotas.CORPORATE?.total || 0) + (sectorQuotas.YACHT_RENTAL?.total || 0);
  }, [sectorQuotas]);

  const remainingWebsiteQuota = useMemo(() => {
    return totalRemainingQuota;
  }, [totalRemainingQuota]);

  const canCreateWebsite = totalRemainingQuota > 0;

  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Undo / Redo Stacks & Toasts
  const [history, setHistory] = useState<SiteData[]>([]);
  const [future, setFuture] = useState<SiteData[]>([]);
  const [toasts, setToasts] = useState<ToastNotification[]>([]);

  const showToast = (message: string, type: 'success' | 'info' | 'warning' | 'error' = 'success') => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    setToasts((prev) => [...prev.slice(-3), { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  };

  const pushToHistory = (site: SiteData) => {
    try {
      setHistory((prev) => [...prev.slice(-30), JSON.parse(JSON.stringify(site))]);
      setFuture([]);
    } catch {
      // ignore serialization errors
    }
  };

  const undo = () => {
    if (history.length === 0 || !activeSite) return;
    const previous = history[history.length - 1];
    setFuture((prev) => [JSON.parse(JSON.stringify(activeSite)), ...prev]);
    setHistory((prev) => prev.slice(0, prev.length - 1));
    setActiveSite(previous);
    setHasUnsavedChanges(true);
    showToast('Değişiklik geri alındı ↶', 'info');
  };

  const redo = () => {
    if (future.length === 0 || !activeSite) return;
    const next = future[0];
    setHistory((prev) => [...prev, JSON.parse(JSON.stringify(activeSite))]);
    setFuture((prev) => prev.slice(1));
    setActiveSite(next);
    setHasUnsavedChanges(true);
    showToast('Değişiklik ileri alındı ↷', 'info');
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeTag = document.activeElement?.tagName.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea' || document.activeElement?.getAttribute('contenteditable') === 'true') {
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') ||
        ((e.ctrlKey || e.metaKey) && e.key === 'y')
      ) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [history, future, activeSite]);

  const loadSiteRelatedData = async (site: SiteData) => {
    try {
      const comms = await communicationService.getCommunications(site.id);
      setCommunications(comms);

      const blogs = await dbService.getSiteBlogs(site.id);
      setBlogCount(blogs.length);

      if (site.industry === 'YACHT_RENTAL') {
        const yachts = await dbService.getSiteYachts(site.id);
        setYachtCount(yachts.length);
        const res = await dbService.getSiteReservations(site.id);
        setReservationCount(res.length);
      } else {
        setYachtCount(0);
        setReservationCount(0);
      }
    } catch (e) {
      console.warn('Could not load site related data:', e);
    }
  };

  const loadCommunications = async () => {
    if (!activeSite) return;
    const comms = await communicationService.getCommunications(activeSite.id);
    setCommunications(comms);
  };

  const createCommunication = async (
    data: Omit<SiteCommunication, 'id' | 'createdAt' | 'status'> & { status?: CommunicationStatus }
  ): Promise<SiteCommunication> => {
    const siteId = activeSite?.id || data.siteId;
    if (!siteId) throw new Error('Site bulunamadı');
    const newMsg = await communicationService.createCommunication(siteId, {
      name: data.name,
      email: data.email,
      phone: data.phone,
      subject: data.subject,
      message: data.message,
      source: data.source,
      status: data.status,
      adminNotes: data.adminNotes,
    });
    setCommunications((prev) => [newMsg, ...prev.filter((c) => c.id !== newMsg.id)]);
    return newMsg;
  };

  const updateCommunicationStatus = async (
    communicationId: string,
    status: CommunicationStatus,
    adminNotes?: string
  ): Promise<void> => {
    if (!activeSite) return;
    await communicationService.updateCommunicationStatus(activeSite.id, communicationId, status, adminNotes);
    setCommunications((prev) =>
      prev.map((c) =>
        c.id === communicationId ? { ...c, status, ...(adminNotes !== undefined ? { adminNotes } : {}) } : c
      )
    );
  };

  const deleteCommunication = async (communicationId: string): Promise<void> => {
    if (!activeSite) return;
    await communicationService.deleteCommunication(activeSite.id, communicationId);
    setCommunications((prev) => prev.filter((c) => c.id !== communicationId));
  };

  // Realtime subscription to communications for active site
  useEffect(() => {
    if (!activeSite?.id) return;
    const unsubscribe = communicationService.subscribeToSiteCommunications(activeSite.id, (updatedComms) => {
      setCommunications(updatedComms);
    });
    return () => {
      unsubscribe();
    };
  }, [activeSite?.id]);

  // Warn user before closing window/tab if unsaved changes exist
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Kaydedilmemiş değişiklikleriniz var. Çıkmak istediğinizden emin misiniz?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load sites when user changes - completely reset state when user logs out or switches
  useEffect(() => {
    if (!currentUser) {
      setSites([]);
      setActiveSite(null);
      setActivePageId('');
      setCommunications([]);
      setBlogCount(0);
      setYachtCount(0);
      setReservationCount(0);
      setSectorQuotas({
        CORPORATE: {
          sector: 'CORPORATE',
          title: 'Kurumsal Web Sitesi',
          allowed: false,
          total: 0,
          used: 0,
          remaining: 0,
          status: 'NO_PLAN',
          planName: 'Paketsiz',
          planPriceTRY: 15000,
          planId: 'PLAN_CORP_ANNUAL',
          features: ['Website oluşturmak için bir paket satın alın.', 'AI Web Sitesi Generator', 'Özel Domain Bağlama'],
        },
        YACHT_RENTAL: {
          sector: 'YACHT_RENTAL',
          title: 'Lüks Yat Kiralama',
          allowed: false,
          total: 0,
          used: 0,
          remaining: 0,
          status: 'NO_PLAN',
          planName: 'Paketsiz',
          planPriceTRY: 36000,
          planId: 'PLAN_YACHT_ANNUAL',
          features: ['Website oluşturmak için bir paket satın alın.', 'Yat Filosu & Donanım', 'Müsaitlik & Rezervasyon'],
        },
      });
      return;
    }
    refreshSites();
  }, [currentUser?.uid]);

  // Debounced Autosave Pipeline: Active Site changes -> Wait 1500ms -> Save to draft
  useEffect(() => {
    if (!activeSite || !hasUnsavedChanges) return;

    setAutosaveStatus('SYNCING');
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(async () => {
      try {
        await dbService.updateSite(activeSite);
        setHasUnsavedChanges(false);
        setAutosaveStatus(navigator.onLine ? 'SAVED' : 'OFFLINE');
      } catch (err) {
        console.error('[Autosave Error]:', err);
        setAutosaveStatus('ERROR');
      }
    }, 1500);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [activeSite, hasUnsavedChanges]);

  const refreshSites = async () => {
    if (!currentUser?.uid) {
      setSites([]);
      setActiveSite(null);
      return;
    }
    setIsLoadingSites(true);
    try {
      if (!QuotaMigrationService.isMigrationCompleted()) {
        await QuotaMigrationService.runMigration();
      }
    } catch (e) {
      console.warn('Migration run error:', e);
    }
    // STRICT TENANT ISOLATION: Query ONLY the sites owned by current authenticated user
    const rawSites = await dbService.getSitesByUser(currentUser.uid);
    const userSites = rawSites.map((site) => ({
      ...site,
      pages: (site.pages || []).map((p) => ({
        ...p,
        sections: p.sections || [],
      })),
    }));
    setSites(userSites);
    await refreshSectorQuotas();
    
    if (userSites.length > 0) {
      const currentExists = activeSite ? userSites.find((s) => s.id === activeSite.id) : null;
      const target = currentExists || userSites[0];
      setActiveSite(target);
      if (target.pages && target.pages.length > 0) {
        setActivePageId(target.pages[0].id);
      } else {
        setActivePageId('');
      }
      loadSiteVersionsForSite(target.id);
      loadSiteRelatedData(target);
    } else {
      setActiveSite(null);
      setActivePageId('');
      setCommunications([]);
      setBlogCount(0);
      setYachtCount(0);
      setReservationCount(0);
    }
    setIsLoadingSites(false);
  };

  const loadSiteVersionsForSite = async (siteId: string) => {
    try {
      const versions = await dbService.getSiteVersions(siteId);
      setSiteVersions(versions);
    } catch {
      setSiteVersions([]);
    }
  };

  const loadSiteVersions = async () => {
    if (!activeSite) return;
    await loadSiteVersionsForSite(activeSite.id);
  };

  const setActiveSiteById = async (siteId: string) => {
    // 1. Immediately activate from in-memory state if available (prevents blank/stale editor state)
    const inMemorySite = sites.find((s) => s.id === siteId);
    if (inMemorySite) {
      setActiveSite(inMemorySite);
      if (inMemorySite.pages && inMemorySite.pages.length > 0) {
        const home = inMemorySite.pages.find((p) => p.isHomePage) || inMemorySite.pages[0];
        setActivePageId(home.id);
      }
      setSelectedSectionId(null);
      setHasUnsavedChanges(false);
      loadSiteVersionsForSite(inMemorySite.id);
      loadSiteRelatedData(inMemorySite);
    }

    // 2. Fetch / refresh latest from database service
    const site = await dbService.getSiteById(siteId);
    if (site) {
      const normalized: SiteData = {
        ...site,
        pages: (site.pages || []).map((p) => ({
          ...p,
          sections: p.sections || [],
        })),
      };
      setActiveSite(normalized);
      if (normalized.pages && normalized.pages.length > 0) {
        setActivePageId((prev) => {
          const exists = normalized.pages.find((p) => p.id === prev);
          if (exists) return prev;
          const home = normalized.pages.find((p) => p.isHomePage) || normalized.pages[0];
          return home.id;
        });
      }
      setSelectedSectionId(null);
      setHasUnsavedChanges(false);
      loadSiteVersionsForSite(normalized.id);
      loadSiteRelatedData(normalized);
    }
  };

  const createNewSite = async (
    name: string,
    subdomain: string,
    industry: IndustryType,
    templateId: string,
    planId: SubscriptionPlanId
  ): Promise<SiteData> => {
    if (!currentUser) throw new Error('Oturum açmanız gerekiyor');
    
    // Quota Enforcement based on isolated Sector Quota
    const isPrivileged = isSuperAdmin || ['super_admin', 'SUPER_ADMIN', 'admin', 'ADMIN'].includes(currentUser.role);
    const targetSector: IndustryType = industry === 'YACHT_RENTAL' ? 'YACHT_RENTAL' : 'CORPORATE';
    const sectorQuota = sectorQuotas[targetSector];

    if (!isPrivileged) {
      const activeNonDeletedSites = sites.filter((s) => !s.isDeleted && s.status !== 'DELETED');
      if (activeNonDeletedSites.length >= 1) {
        throw new Error(
          'Bu hesapta zaten aktif bir website bulunmaktadır. Adiosoft sisteminde 1 hesapta aynı anda en fazla 1 aktif website barındırılabilir. Yeni bir site kurmak için lütfen önce mevcut sitenizi tamamen silin veya yeni bir Adiosoft hesabı oluşturun.'
        );
      }

      if (!sectorQuota.allowed || sectorQuota.total === 0) {
        throw new Error(
          `Bu sektör (${targetSector === 'YACHT_RENTAL' ? 'Lüks Yat Kiralama' : 'Kurumsal Web Sitesi'}) için aktif bir website paketiniz bulunmuyor. Lütfen önce ilgili paketi satın alın.`
        );
      }
      if (sectorQuota.remaining <= 0 || sectorQuota.used >= sectorQuota.total) {
        throw new Error(
          `${sectorQuota.title} paketinizdeki website oluşturma hakkınız doldu (${sectorQuota.used} / ${sectorQuota.total}). Yeni bir site açmak için lütfen mevcut sitelerinizden birini silin veya ek paket edinin.`
        );
      }
    }

    const created = await dbService.createSite(currentUser.uid, name, subdomain, industry, templateId, planId);
    await refreshSites();
    await refreshSectorQuotas();
    setActiveSite(created);
    if (created.pages && created.pages.length > 0) {
      setActivePageId(created.pages[0].id);
    }
    loadSiteRelatedData(created);
    return created;
  };

  const createSiteWithAI = async (
    sitePlan: AISitePlan,
    planId?: SubscriptionPlanId
  ): Promise<SiteData> => {
    const ownerId = currentUser?.uid || 'guest_user';
    const isPrivileged = isSuperAdmin || (currentUser && ['super_admin', 'SUPER_ADMIN', 'admin', 'ADMIN'].includes(currentUser.role));
    const targetSector: IndustryType = sitePlan.industry === 'YACHT_RENTAL' ? 'YACHT_RENTAL' : 'CORPORATE';
    const sectorQuota = sectorQuotas[targetSector];

    if (!isPrivileged && ownerId !== 'guest_user') {
      const activeNonDeletedSites = sites.filter((s) => !s.isDeleted && s.status !== 'DELETED');
      if (activeNonDeletedSites.length >= 1) {
        throw new Error(
          'Bu hesapta zaten aktif bir website bulunmaktadır. Adiosoft sisteminde 1 hesapta aynı anda en fazla 1 aktif website barındırılabilir. Yeni bir site kurmak için lütfen önce mevcut sitenizi tamamen silin veya yeni bir Adiosoft hesabı oluşturun.'
        );
      }

      if (!sectorQuota.allowed || sectorQuota.total === 0) {
        throw new Error(
          `Bu sektör (${targetSector === 'YACHT_RENTAL' ? 'Lüks Yat Kiralama' : 'Kurumsal Web Sitesi'}) için aktif bir website paketiniz bulunmuyor. Lütfen önce ilgili paketi satın alın.`
        );
      }
      if (sectorQuota.remaining <= 0 || sectorQuota.used >= sectorQuota.total) {
        throw new Error(
          `${sectorQuota.title} paketinizdeki website oluşturma hakkınız doldu (${sectorQuota.used} / ${sectorQuota.total}). Yeni bir site açmak için lütfen mevcut sitelerinizden birini silin veya ek paket edinin.`
        );
      }
    }

    const chosenPlan =
      planId ||
      (targetSector === 'YACHT_RENTAL' ? 'PLAN_YACHT_ANNUAL' : 'PLAN_CORP_ANNUAL');
    const created = await dbService.createSiteFromAISitePlan(ownerId, sitePlan, chosenPlan);
    await refreshSites();
    await refreshSectorQuotas();
    setActiveSite(created);
    if (created.pages && created.pages.length > 0) {
      setActivePageId(created.pages[0].id);
    }
    setSelectedSectionId(null);
    setHasUnsavedChanges(false);
    loadSiteRelatedData(created);
    return created;
  };

  const updateSiteData = async (updatedFields: Partial<SiteData>): Promise<SiteData> => {
    if (!activeSite) throw new Error('Aktif site bulunamadı');
    const merged: SiteData = {
      ...activeSite,
      ...updatedFields,
      updatedAt: Date.now(),
    };
    const saved = await dbService.updateSite(merged);
    setActiveSite(saved);
    setSites((prev) => prev.map((s) => (s.id === saved.id ? saved : s)));
    setHasUnsavedChanges(false);
    setAutosaveStatus('SAVED');
    return saved;
  };

  const deleteCurrentSite = async () => {
    if (!activeSite) return;
    await dbService.deleteSite(activeSite.id);
    await refreshSites();
    await refreshSectorQuotas();
  };

  const saveDraft = async () => {
    if (!activeSite) return;
    setAutosaveStatus('SYNCING');
    try {
      const saved = await dbService.updateSite(activeSite);
      setActiveSite(saved);
      setHasUnsavedChanges(false);
      setAutosaveStatus('SAVED');
    } catch (err) {
      setAutosaveStatus('ERROR');
      throw err;
    }
  };

  const publishCurrentSite = async () => {
    if (!activeSite) return;
    setIsPublishing(true);
    try {
      const report = await PublishPipelineService.executePublish(
        activeSite.id,
        currentUser?.uid || 'user'
      );

      if (!report.success) {
        showToast(report.error || 'Yayınlama başarısız oldu.', 'error');
        return;
      }

      const updatedSite = await dbService.getSiteById(activeSite.id);
      if (updatedSite) {
        setActiveSite(updatedSite);
        setSites((prev) => prev.map((s) => (s.id === updatedSite.id ? updatedSite : s)));
      }

      setHasUnsavedChanges(false);
      setAutosaveStatus('SAVED');
      await loadSiteVersionsForSite(activeSite.id);
      showToast('Tebrikler! Web siteniz başarıyla canlıya alındı.', 'success');
      
      // Fire celebration confetti
      try {
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.6 },
        });
      } catch {
        // Confetti optional
      }
    } catch (err: any) {
      console.warn('Publish error caught:', err);
      showToast(err.message || 'Yayınlama sırasında bir hata oluştu.', 'error');
    } finally {
      setIsPublishing(false);
    }
  };

  const unpublishCurrentSite = async () => {
    if (!activeSite) return;
    setIsPublishing(true);
    try {
      const updatedSite = await dbService.unpublishSite(activeSite.id, currentUser?.uid || 'user');
      setActiveSite(updatedSite);
      setSites((prev) => prev.map((s) => (s.id === updatedSite.id ? updatedSite : s)));
      showToast('Web sitesi başarıyla yayından kaldırıldı (Unpublished).', 'info');
    } catch (err: any) {
      showToast(err.message || 'Yayından kaldırılırken hata oluştu.', 'error');
    } finally {
      setIsPublishing(false);
    }
  };

  const rollbackToVersion = async (versionId: string) => {
    if (!activeSite || !currentUser) return;
    setIsPublishing(true);
    try {
      await PublishPipelineService.rollbackToVersion(activeSite.id, versionId, currentUser.uid);
      await refreshSites();
    } finally {
      setIsPublishing(false);
    }
  };

  // Section Operations on current active page
  const addSectionToActivePage = (type: SectionType, targetIndex?: number) => {
    if (!activeSite || !activeSite.pages) return;

    const pageIndex = activeSite.pages.findIndex((p) => p && p.id === activePageId);
    if (pageIndex === -1) return;

    const defaultContentMap: Partial<Record<SectionType, any>> = {
      HEADER: { title: activeSite.name, buttonText: 'İletişim', buttonLink: '/iletisim' },
      HERO: {
        badge: 'PROFESYONEL VE MODERN',
        title: 'İşletmenizi Dijital Dünyaya Taşıyın',
        description: 'En son teknoloji ve kurumsal çözümlerimizle hedeflerinize ulaşın.',
        buttonText: 'Hemen Başlayın',
        buttonLink: '/iletisim',
        bgImageUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop',
        bgOverlayOpacity: 70,
      },
      ABOUT: {
        badge: 'HAKKIMIZDA',
        title: 'Tecrübemiz ve Değerlerimiz',
        description: 'Müşteri memnuniyetini ön planda tutan profesyonel yaklaşımımızla sektörde fark yaratıyoruz.',
        imageUrl: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1200&auto=format&fit=crop',
        buttonText: 'Daha Fazla Bilgi',
        buttonLink: '/hakkimizda',
      },
      SERVICES: {
        badge: 'HİZMETLERİMİZ',
        title: 'Uzman Olduğumuz Alanlar',
        description: 'İhtiyaçlarınıza özel tasarlanmış profesyonel hizmet paketleri.',
        items: [
          { id: '1', title: 'Danışmanlık & Strateji', description: 'Kurumunuza özel çözümler ve yol haritası.' },
          { id: '2', title: 'Operasyonel Yönetim', description: 'Verimli ve sürdürülebilir süreç optimizasyonu.' },
          { id: '3', title: 'Teknoloji & Altyapı', description: 'Modern dijital çözümler ve entegrasyon.' },
        ],
      },
      TEAM: {
        badge: 'UZMAN KADRO',
        title: 'Ekibimizle Tanışın',
        items: [
          { id: '1', title: 'Ahmet Yılmaz', subtitle: 'Kurucu & Yönetici', description: '15 yıllık sektör tecrübesi.' },
          { id: '2', title: 'Zeynep Kaya', subtitle: 'Operasyon Direktörü', description: 'Müşteri deneyimi uzmanı.' },
        ],
      },
      REFERENCES: {
        badge: 'İŞ ORTAKLARIMIZ',
        title: 'Bize Güvenen Markalar',
        items: [{ id: '1', title: 'Global Tech' }, { id: '2', title: 'Acme Holding' }, { id: '3', title: 'Nexus Corp' }],
      },
      TESTIMONIALS: {
        badge: 'YORUMLAR',
        title: 'Müşterilerimizin Deneyimleri',
        items: [
          { id: '1', title: 'Harika Hizmet', description: 'İş süreçlerimizde büyük fark yarattılar.', author: 'Mehmet Demir', rating: 5 },
        ],
      },
      GALLERY: {
        badge: 'GALERİ',
        title: 'Projelerimizden Kareler',
        items: [
          { id: '1', title: 'Merkez Ofis', imageUrl: 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=600&auto=format&fit=crop' },
          { id: '2', title: 'Proje Sahası', imageUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=600&auto=format&fit=crop' },
        ],
      },
      FAQ: {
        badge: 'SIKÇA SORULANLAR',
        title: 'Merak Edilen Sorular',
        items: [
          { id: '1', question: 'Hizmet süreciniz nasıl işliyor?', answer: 'İlk analiz görüşmesinden sonra proje planı hazırlanarak adımlar başlatılır.' },
          { id: '2', question: 'Fiyatlandırma politikanız nedir?', answer: 'İhtiyaca ve kapsamına göre şeffaf fiyatlandırma teklifleri sunulur.' },
        ],
      },
      CONTACT: {
        badge: 'İLETİŞİM',
        title: 'Bizimle İletişime Geçin',
        description: 'Projeleriniz ve sorularınız için uzman ekibimiz 7/24 yanınızda.',
        email: 'info@sirketiniz.com',
        phone: '+90 (212) 555 0123',
        address: 'Levent Maslak Caddesi No:42 Beşiktaş / İstanbul',
      },
      FOOTER: {
        title: activeSite.name,
        description: 'Güvenilir kurumsal iş ortağınız.',
        copyrightText: `© ${new Date().getFullYear()} ${activeSite.name}. Tüm hakları saklıdır.`,
        phone: '+90 212 555 0000',
        email: 'info@sirketiniz.com',
      },
      YACHT_FLEET: {
        badge: 'LÜKS FİLO',
        title: 'Mavi Yolculuk Yat Filomuz',
        description: 'Ege ve Akdeniz’in en seçkin motor yat ve gulet seçenekleri.',
      },
      RESERVATION_CTA: {
        badge: 'REZERVASYON',
        title: 'Erken Rezervasyon Talebi Oluşturun',
        description: 'Hayalinizdeki mavi yolculuk için hemen teklif alın.',
      },
      STATS: {
        badge: 'RAKAMLARLA BİZ',
        title: 'Başarılarımız ve Etki Alanımız',
        items: [
          { id: '1', number: '150+', label: 'Tamamlanan Proje' },
          { id: '2', number: '99%', label: 'Müşteri Memnuniyeti' },
          { id: '3', number: '15+', label: 'Yıllık Sektör Deneyimi' },
        ],
      },
      PRICING: {
        badge: 'FİYATLANDIRMA',
        title: 'Şeffaf ve Esnek Paketler',
      },
      BLOG_PREVIEW: {
        badge: 'GÜNCEL',
        title: 'Sektörel Yazılar & Haberler',
      },
    };

    const safeType = type || 'custom';
    const newSection: PageSection = {
      id: `sec_${safeType.toLowerCase()}_${Date.now().toString(36)}`,
      type,
      name: `${safeType.charAt(0).toUpperCase() + safeType.slice(1).toLowerCase()} Bölümü`,
      isVisible: true,
      content: defaultContentMap[type] || {},
      style: {
        paddingTop: '64px',
        paddingBottom: '64px',
        backgroundColor: type === 'FOOTER' ? '#0F172A' : '#FFFFFF',
        textColor: type === 'FOOTER' ? '#94A3B8' : '#0F172A',
      },
    };

    if (activeSite) pushToHistory(activeSite);
    const updatedPages = [...activeSite.pages];
    const page = { ...updatedPages[pageIndex] };
    const currentSections = [...(page.sections || [])];

    if (typeof targetIndex === 'number' && targetIndex >= 0 && targetIndex <= currentSections.length) {
      currentSections.splice(targetIndex, 0, newSection);
    } else {
      currentSections.push(newSection);
    }

    page.sections = currentSections;
    updatedPages[pageIndex] = page;

    setActiveSite({
      ...activeSite,
      pages: updatedPages,
    });
    setSelectedSectionId(newSection.id);
    setHasUnsavedChanges(true);
    showToast('Yeni bölüm eklendi ✓', 'success');
  };

  const addCustomSectionToActivePage = (customSection: PageSection, targetIndex?: number) => {
    if (!activeSite || !activeSite.pages) return;

    const pageIndex = activeSite.pages.findIndex((p) => p && p.id === activePageId);
    if (pageIndex === -1) return;

    pushToHistory(activeSite);
    const updatedPages = [...activeSite.pages];
    const page = { ...updatedPages[pageIndex] };
    const currentSections = [...(page.sections || [])];

    if (typeof targetIndex === 'number' && targetIndex >= 0 && targetIndex <= currentSections.length) {
      currentSections.splice(targetIndex, 0, customSection);
    } else {
      currentSections.push(customSection);
    }

    page.sections = currentSections;
    updatedPages[pageIndex] = page;

    setActiveSite({
      ...activeSite,
      pages: updatedPages,
    });
    setSelectedSectionId(customSection.id);
    setHasUnsavedChanges(true);
    showToast('Özel bölüm eklendi ✓', 'success');
  };

  const updateSectionInActivePage = (sectionId: string, contentUpdates?: any, styleUpdates?: any) => {
    if (!activeSite || !activeSite.pages) return;

    const pageIndex = activeSite.pages.findIndex((p) => p && p.id === activePageId);
    if (pageIndex === -1) return;

    pushToHistory(activeSite);
    const updatedPages = [...activeSite.pages];
    const page = { ...updatedPages[pageIndex] };
    const currentSections = [...(page.sections || [])];
    const secIndex = currentSections.findIndex((s) => s.id === sectionId);
    if (secIndex === -1) return;

    const sec = { ...currentSections[secIndex] };
    if (contentUpdates) {
      sec.content = { ...sec.content, ...contentUpdates };
    }
    if (styleUpdates) {
      sec.style = { ...sec.style, ...styleUpdates };
    }

    currentSections[secIndex] = sec;
    page.sections = currentSections;
    updatedPages[pageIndex] = page;

    setActiveSite({
      ...activeSite,
      pages: updatedPages,
    });
    setHasUnsavedChanges(true);
  };

  const moveSectionInActivePage = (fromIndex: number, toIndex: number) => {
    if (!activeSite || !activeSite.pages) return;
    const pageIndex = activeSite.pages.findIndex((p) => p && p.id === activePageId);
    if (pageIndex === -1) return;

    pushToHistory(activeSite);
    const updatedPages = [...activeSite.pages];
    const page = { ...updatedPages[pageIndex] };
    const sections = [...(page.sections || [])];

    if (toIndex < 0 || toIndex >= sections.length) return;

    const [moved] = sections.splice(fromIndex, 1);
    sections.splice(toIndex, 0, moved);

    page.sections = sections;
    updatedPages[pageIndex] = page;

    setActiveSite({
      ...activeSite,
      pages: updatedPages,
    });
    setHasUnsavedChanges(true);
    showToast('Bölüm sırası güncellendi ✓', 'info');
  };

  const duplicateSectionInActivePage = (sectionId: string) => {
    if (!activeSite || !activeSite.pages) return;
    const pageIndex = activeSite.pages.findIndex((p) => p && p.id === activePageId);
    if (pageIndex === -1) return;

    pushToHistory(activeSite);
    const updatedPages = [...activeSite.pages];
    const page = { ...updatedPages[pageIndex] };
    const currentSections = [...(page.sections || [])];
    const secIndex = currentSections.findIndex((s) => s.id === sectionId);
    if (secIndex === -1) return;

    const targetSec = currentSections[secIndex];
    const secType = targetSec.type || 'section';
    const duplicate: PageSection = {
      ...JSON.parse(JSON.stringify(targetSec)),
      id: `sec_${secType.toLowerCase()}_${Date.now().toString(36)}`,
      name: `${targetSec.name || 'Bölüm'} (Kopya)`,
    };

    currentSections.splice(secIndex + 1, 0, duplicate);
    page.sections = currentSections;
    updatedPages[pageIndex] = page;

    setActiveSite({
      ...activeSite,
      pages: updatedPages,
    });
    setSelectedSectionId(duplicate.id);
    setHasUnsavedChanges(true);
    showToast('Bölüm çoğaltıldı ✓', 'success');
  };

  const toggleSectionVisibility = (sectionId: string) => {
    if (!activeSite || !activeSite.pages) return;
    const pageIndex = activeSite.pages.findIndex((p) => p && p.id === activePageId);
    if (pageIndex === -1) return;

    pushToHistory(activeSite);
    const updatedPages = [...activeSite.pages];
    const page = { ...updatedPages[pageIndex] };
    const currentSections = [...(page.sections || [])];
    const secIndex = currentSections.findIndex((s) => s.id === sectionId);
    if (secIndex === -1) return;

    currentSections[secIndex] = {
      ...currentSections[secIndex],
      isVisible: !currentSections[secIndex].isVisible,
    };
    page.sections = currentSections;
    updatedPages[pageIndex] = page;

    setActiveSite({
      ...activeSite,
      pages: updatedPages,
    });
    setHasUnsavedChanges(true);
    showToast(currentSections[secIndex].isVisible ? 'Bölüm görünür yapıldı ✓' : 'Bölüm gizlendi', 'info');
  };

  const deleteSectionInActivePage = (sectionId: string) => {
    if (!activeSite || !activeSite.pages) return;
    const pageIndex = activeSite.pages.findIndex((p) => p && p.id === activePageId);
    if (pageIndex === -1) return;

    pushToHistory(activeSite);
    const updatedPages = [...activeSite.pages];
    const page = { ...updatedPages[pageIndex] };
    page.sections = (page.sections || []).filter((s) => s.id !== sectionId);
    updatedPages[pageIndex] = page;

    setActiveSite({
      ...activeSite,
      pages: updatedPages,
    });
    if (selectedSectionId === sectionId) {
      setSelectedSectionId(null);
    }
    setHasUnsavedChanges(true);
    showToast('Bölüm silindi', 'info');
  };

  // Page Operations
  const addPageToSite = (title: string, slug: string, seoTitle?: string, seoDescription?: string) => {
    if (!activeSite) return;

    const cleanSlug = slug.startsWith('/') ? slug : `/${slug}`;
    const existingPages = activeSite.pages || [];
    const newPage: SitePage = {
      id: `page_${Date.now().toString(36)}`,
      siteId: activeSite.id,
      title,
      slug: cleanSlug,
      seoTitle: seoTitle || `${title} — ${activeSite.name}`,
      seoDescription: seoDescription || `${activeSite.name} ${title} sayfası.`,
      isHomePage: false,
      isPublished: true,
      order: existingPages.length,
      sections: [
        {
          id: `sec_hdr_${Date.now().toString(36)}`,
          type: 'HEADER',
          name: 'Üst Menü',
          isVisible: true,
          content: { title: activeSite.name, buttonText: 'İletişim', buttonLink: '/iletisim' },
          style: { backgroundColor: '#FFFFFF', textColor: '#0F172A' },
        },
        {
          id: `sec_hero_${Date.now().toString(36)}`,
          type: 'HERO',
          name: 'Sayfa Başlığı',
          isVisible: true,
          content: {
            title,
            description: `${title} hakkında detaylı bilgiler ve kurumsal içerikler.`,
          },
          style: { paddingTop: '80px', paddingBottom: '80px', backgroundColor: '#F8FAFC' },
        },
        {
          id: `sec_ftr_${Date.now().toString(36)}`,
          type: 'FOOTER',
          name: 'Alt Bilgi',
          isVisible: true,
          content: { title: activeSite.name, description: 'Tüm hakları saklıdır.' },
          style: { backgroundColor: '#0F172A', textColor: '#94A3B8' },
        },
      ],
    };

    const updatedPages = [...existingPages, newPage];
    setActiveSite({
      ...activeSite,
      pages: updatedPages,
    });
    setActivePageId(newPage.id);
    setHasUnsavedChanges(true);
  };

  const updatePageInSite = (pageId: string, updates: Partial<SitePage>) => {
    if (!activeSite) return;
    const updatedPages = (activeSite.pages || []).map((p) => (p.id === pageId ? { ...p, ...updates } : p));
    setActiveSite({
      ...activeSite,
      pages: updatedPages,
    });
    setHasUnsavedChanges(true);
  };

  const deletePageFromSite = (pageId: string) => {
    if (!activeSite) return;
    const existingPages = activeSite.pages || [];
    const targetPage = existingPages.find((p) => p.id === pageId);
    if (targetPage?.isHomePage) {
      showToast('Ana sayfa silinemez!', 'error');
      return;
    }

    const updatedPages = existingPages.filter((p) => p.id !== pageId);
    setActiveSite({
      ...activeSite,
      pages: updatedPages,
    });
    if (activePageId === pageId && updatedPages.length > 0) {
      setActivePageId(updatedPages[0].id);
    }
    setHasUnsavedChanges(true);
  };

  const deleteSite = async (siteId: string) => {
    await dbService.deleteSite(siteId);
    if (activeSite?.id === siteId) {
      setActiveSite(null);
      setActivePageId(null);
    }
    await refreshSites();
    await refreshSectorQuotas();
  };

  return (
    <SiteContext.Provider
      value={{
        sites,
        activeSite,
        activePageId,
        selectedSectionId,
        deviceMode,
        testViewportWidth,
        isPreviewMode,
        isPublishing,
        hasUnsavedChanges,
        isLoadingSites,
        autosaveStatus,
        siteVersions,
        setActiveSite,
        setActiveSiteById,
        setActiveSiteId: setActiveSiteById,
        setActivePageId,
        setSelectedSectionId,
        setDeviceMode,
        setTestViewportWidth,
        setIsPreviewMode,
        createNewSite,
        createSite: createNewSite,
        createSiteWithAI,
        updateSiteData,
        deleteCurrentSite,
        deleteSite,
        publishCurrentSite,
        unpublishCurrentSite,
        rollbackToVersion,
        loadSiteVersions,
        addSectionToActivePage,
        addCustomSectionToActivePage,
        updateSectionInActivePage,
        moveSectionInActivePage,
        duplicateSectionInActivePage,
        toggleSectionVisibility,
        deleteSectionInActivePage,
        addPageToSite,
        updatePageInSite,
        deletePageFromSite,
        saveDraft,
        refreshSites,
        reloadSites: refreshSites,
        undo,
        redo,
        canUndo: history.length > 0,
        canRedo: future.length > 0,
        toasts,
        showToast,
        communications,
        unreadCommunicationsCount,
        communicationCount,
        loadCommunications,
        createCommunication,
        updateCommunicationStatus,
        deleteCommunication,
        yachtCount,
        reservationCount,
        blogCount,
        websiteLimit,
        remainingWebsiteQuota,
        canCreateWebsite,
        sectorQuotas,
        canCreateSectorWebsite,
        totalRemainingQuota,
        refreshSectorQuotas,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

export default SiteContext;
